import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema } from "./schemas";
import type { LoginForm } from "./schemas";
import { useDI } from "@infra/di/useDI";
import { useLoginViewModel } from "./LoginViewModel";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { AuthActions, AuthBrand, AuthCard, AuthContent, AuthHeader, AuthPage } from "../layout";
import {
  Alert, Button, Stack, TextField, IconButton,
  InputAdornment, Link as MuiLink
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useDI();
  const { state, intents } = useLoginViewModel(login);

  const isLoading = state.status === "loading";
  const error = state.status === "error" ? state.message : null;

  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (values: LoginForm) => {
    const result = await intents.submit(values);
    if (result.type === "SUCCESS") {
      navigate("/dashboard");
    }
  };

  return (
    <AuthPage>
      <AuthCard>
        <AuthContent>
          <AuthBrand />
          <AuthHeader
            title="Anmelden"
            subtitle="Melden Sie sich mit Ihrem Logami-Zugang an."
          />

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Stack
            component="form"
            spacing={2.5}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            <TextField
              label="E-Mail-Adresse"
              type="email"
              autoComplete="email"
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              fullWidth
              {...register("email")}
            />

            <Stack spacing={1}>
              <TextField
                label="Passwort"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                error={Boolean(errors.password)}
                helperText={errors.password?.message}
                fullWidth
                {...register("password")}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword ? "Passwort ausblenden" : "Passwort anzeigen"
                          }
                          onClick={() => setShowPassword((value) => !value)}
                          onMouseDown={(event) => event.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <MuiLink
                component={RouterLink}
                to="/password/reset/init"
                variant="body2"
                alignSelf="flex-end"
                underline="hover"
              >
                Passwort vergessen?
              </MuiLink>
            </Stack>

            <AuthActions align="between">
              <Button component={RouterLink} to="/signup" variant="text">
                Konto erstellen
              </Button>

              <Button disabled={isLoading} type="submit" variant="contained">
                {isLoading ? "Anmeldung läuft..." : "Anmelden"}
              </Button>
            </AuthActions>
          </Stack>
        </AuthContent>
      </AuthCard>
    </AuthPage>
  );
}
